/**
 * Composition root for the in-world experience.
 *
 * `index.ts` owns the world and the entry gate; everything that happens once
 * passthrough is running is wired here — panels, their handlers, and the
 * subscriptions that keep them in step with the conversation.
 *
 * Panels are long-lived and rewritten in place rather than spawned per message.
 * Every `PanelUI` re-parses its markup on creation and the panel system walks
 * the scene graph every frame to tick live documents, so a panel per chat
 * message would cost more each turn.
 */
import type { World } from '@iwsdk/core';
import {
  DockMode,
  createUIWindow,
  type IwsdkSceneHost,
  type PanelHandle,
  type WindowManager,
} from '@realitycollective/iwsdk-uiextensions';

import { CONFIG } from './config.js';
import { GuideSystem } from './GuideSystem.js';
import { PlacementSystem } from './placement/PlacementSystem.js';
import { placementStore } from './placement/placementStore.js';
import { chat, initChat } from './chat/chatRuntime.js';
import { uiLog } from './ui/uiLog.js';

export interface ExperienceDeps {
  world: World;
  host: IwsdkSceneHost;
  windows: WindowManager;
  apiBaseUrl: string;
}

export const PANEL = {
  bubble: 'guide-bubble',
  compose: 'guide-compose',
} as const;

/**
 * Panel markup, parsed at runtime from `public/`.
 *
 * Named here rather than inline at each call site so the boot-time preflight
 * checks the same URLs the panels actually load — see `ui/panelPreflight.ts`.
 */
const SOURCE = {
  bubble: '/ui/bubble.uikitml',
  compose: '/ui/compose.uikitml',
} as const;

export const PANEL_SOURCE_URLS: readonly string[] = Object.values(SOURCE);

export function installGuideExperience(deps: ExperienceDeps): void {
  const { world, host, apiBaseUrl } = deps;

  initChat(apiBaseUrl);

  world.registerSystem(PlacementSystem).registerSystem(GuideSystem);

  // Spawned when the guide is placed, not at session start. Until then the
  // room is meant to be empty apart from the reticle: a window floating in
  // front of someone who is trying to aim at a table is in the way, and a chat
  // panel before there is anyone to chat to has nothing to say.
  let spawned = false;
  const unsubscribe = placementStore.subscribe((state) => {
    if (spawned || state.phase !== 'placed') return;
    spawned = true;
    unsubscribe();
    uiLog.push('guide placed — spawning in-world UI');
    spawnPanels(world);
  });

  host.onPanelReady(({ id, panel }) => {
    uiLog.push(`panel ready: ${id}`);
    switch (id) {
      case PANEL.bubble:
        wireBubblePanel(panel);
        break;
      case PANEL.compose:
        wireComposePanel(panel);
        break;
      default:
        break;
    }
  });
}

// ── panel spawning ────────────────────────────────────────────────────────

function spawnPanels(world: World): void {
  // World-locked: it is reparented under the guide as soon as it is ready, so
  // any follow behaviour here would fight that.
  createUIWindow(world, {
    id: PANEL.bubble,
    title: 'Guide',
    config: SOURCE.bubble,
    dockMode: DockMode.WorldLocked,
    movable: false,
    closable: false,
    minimizable: false,
    pinnable: false,
    maxWidth: CONFIG.ui.bubbleSize[0],
    maxHeight: CONFIG.ui.bubbleSize[1],
  });

  createUIWindow(world, {
    id: PANEL.compose,
    title: 'Ask',
    config: SOURCE.compose,
    // Body-follow rather than head-lock: it stays with the user as they move
    // and turn, without swimming with every glance.
    dockMode: DockMode.BodyFollow,
    // Bottom edge of view — and "bottom edge" has to be measured against the
    // HEADSET, not a monitor. Quest 3 is roughly 90° vertically, so at 0.85 m
    // the bottom of view is about 0.85 m below the eye line. An earlier value
    // of -0.46 was sized against a ~50° desktop FOV and landed the panel in the
    // middle of the view, reading as a window floating in front of the user.
    //
    // -0.62 is ~36° down: clear of the guide and the resting gaze, still an
    // easy glance rather than a neck-bend. It also keeps the Quest system
    // keyboard — which renders in front of the focused field with no
    // positioning API — below the conversation instead of across it.
    followOffset: [0, -0.62, -0.85],
    movable: true,
    closable: false,
    minimizable: true,
    pinnable: true,
    // Smaller than the bubble. This is a control surface, not the content, and
    // at under a metre a large panel dominates the view however low it sits.
    maxWidth: 0.5,
    maxHeight: 0.36,
  });
}

// ── speech bubble ─────────────────────────────────────────────────────────

function wireBubblePanel(panel: PanelHandle): void {
  // The bubble's entity is adopted by GuideSystem via an ECS query — this
  // function owns only the panel's content and handlers.
  const text = panel.getElementById('bubble-text');
  const thinking = panel.getElementById('bubble-thinking');
  const error = panel.getElementById('bubble-error');

  const followUps = [1, 2, 3].map((n) => ({
    row: panel.getElementById(`followup-${n}`),
    label: panel.getElementById(`followup-${n}-text`),
    ordinal: n,
  }));

  for (const { row, ordinal } of followUps) {
    row?.addEventListener('click', () => chat.chooseFollowUp(ordinal));
  }

  // Tapping the bubble skips to the end of the reply — the same affordance the
  // reference project offers, and welcome when the guide is being verbose.
  text?.addEventListener('click', () => chat.playback.complete());

  const render = (): void => {
    const conversation = chat.conversation.getSnapshot();
    const reveal = chat.playback.getSnapshot();
    const isThinking = conversation.status === 'sending' && reveal.visibleText.length === 0;

    text?.setProperties({ text: reveal.visibleText });
    thinking?.setProperties({ text: isThinking ? '…' : '' });
    error?.setProperties({ text: conversation.status === 'error' ? (conversation.error ?? '') : '' });

    // Offered only once the guide has stopped speaking, so they read as options
    // rather than the guide interrupting itself.
    const offered = reveal.phase === 'finished' ? chat.conversation.currentFollowUps() : [];
    for (const { label, ordinal } of followUps) {
      label?.setProperties({ text: offered[ordinal - 1] ?? '' });
    }
  };

  chat.conversation.subscribe(render);
  chat.playback.subscribe(render);
}

// ── compose panel ─────────────────────────────────────────────────────────

function wireComposePanel(panel: PanelHandle): void {
  const input = panel.getElementById('compose-input');
  const send = panel.getElementById('compose-send');
  const divider = panel.getElementById('compose-divider');

  let draft = '';
  input?.setProperties({
    onValueChange: (value: string) => {
      draft = value;
    },
  });

  const submit = (): void => {
    const text = draft.trim();
    if (!text) return;
    chat.ask(text);
    draft = '';
    // Each keyboard session overwrites the field anyway; clearing keeps the
    // panel honest about the fact that this is "ask a new question".
    input?.setProperties({ value: '' });
  };
  send?.addEventListener('click', submit);

  const suggestions = [1, 2, 3].map((n) => ({
    row: panel.getElementById(`suggestion-${n}`),
    label: panel.getElementById(`suggestion-${n}-text`),
    index: n - 1,
  }));

  let current: readonly string[] = [];
  for (const { row, index } of suggestions) {
    row?.addEventListener('click', () => {
      const text = current[index];
      if (text) chat.ask(text);
    });
  }

  chat.suggestions.subscribe((next) => {
    current = next;
    for (const { label, index } of suggestions) {
      label?.setProperties({ text: next[index] ?? '' });
    }
    divider?.setProperties({ text: next.length > 0 ? 'OR TRY' : '' });
  });
}
